// Community Projects Data - Beyond employment, holistic community support
export const CommunityProjectsData = [
  {
    id: "advocacy-services",
    title: "Advocacy Services",
    shortDescription: "Your voice, your rights, our support",
    description:
      "Professional advocacy to help you navigate complex systems, understand your rights, and ensure fair treatment across housing, benefits, immigration, and healthcare.",
    projectType: "advocacy",
    whoThisHelps: [
      "Asylum seekers navigating UK systems",
      "Those facing housing issues or homelessness",
      "People struggling with benefits applications/sanctions",
      "Anyone experiencing discrimination or unfair treatment",
      "Non-native English speakers needing representation",
    ],
    servicesProvided: [
      {
        service: "Immigration & Asylum Support",
        description:
          "Guidance through asylum process, understanding Home Office letters, connecting to legal support, preparing for interviews",
        casesSupported2024: "40+",
        outcomes: "Successful applications, emergency accommodation secured",
      },
      {
        service: "Housing Advocacy",
        description:
          "Challenging unfair evictions, bidding for social housing, reporting unsafe conditions, understanding tenant rights",
        casesSupported2024: "35+",
        outcomes: "Housing secured, repairs completed, homelessness prevented",
      },
      {
        service: "Benefits Advocacy",
        description:
          "Mandatory reconsideration requests, appeal support, challenging sanctions, maximizing entitlements",
        casesSupported2024: "50+",
        outcomes: "£45,000+ in backdated benefits secured for clients",
      },
      {
        service: "Healthcare Access",
        description:
          "GP registration support, understanding NHS entitlements, accessing mental health services, interpreter requests",
        casesSupported2024: "25+",
        outcomes: "Families registered with GPs, treatment accessed",
      },
      {
        service: "Education & SEND Advocacy",
        description:
          "School admissions, challenging exclusions, accessing SEN support, understanding education rights",
        casesSupported2024: "20+",
        outcomes: "School places secured, SEN assessments obtained",
      },
    ],
    totalImpact2024: {
      casesSupported: "170+",
      financialBenefitsSecured: "£45,000+",
      housingOutcomes: "12 families secured stable housing",
      systemsNavigated:
        "Home Office, DWP, Local Housing Authorities, NHS, Schools",
    },
    caseStudy: {
      situation:
        "Single mother of 3, asylum seeker from Eritrea, facing eviction from temporary accommodation",
      barriers:
        "Limited English, no understanding of rights, 2 weeks to appeal, children at risk of homelessness",
      intervention:
        "APT advocate contacted housing authority, gathered evidence of children's school stability, requested extension for legal representation, connected family to emergency legal aid",
      outcome:
        "Eviction halted, family secured 12-month temporary housing, children remained in same schools, mother referred to ESOL classes",
      quote:
        "I thought we would be on the street. APT fought for us when I couldn't fight for myself.",
    },
partnerOrganizations: [
       "Newham College",
       "Citizens Advice",
       "Shpresa Programme",
       "Mayor of London",
       "Local SME Businesses",
       "Integration Hubs",
       "North & East London CVS"
     ],
    accessInfo: {
      cost: "Free for all community members",
      languages: "Support in Arabic, Albanian, Tigrinya, Bengali, Urdu",
      contactMethods: "Phone, email, drop-in (Mon-Fri 10am-4pm)",
      urgentCases:
        "Emergency appointments available for evictions/sanctions",
      referrals: "Self-referral or through partner organizations",
    },
  },

  {
    id: "community-outreach",
    title: "Community Outreach",
    shortDescription: "Meeting people where they are, building trust and connections",
    description:
      "Proactive outreach to hard-to-reach communities, breaking down barriers to accessing support through presence, trust-building, and culturally sensitive engagement.",
    projectType: "outreach",
    whoWeReach: [
      "Isolated individuals and families",
      "Those unaware of available support",
      "People with language barriers",
      "Communities with low trust in institutions",
      "Residents of temporary accommodation",
    ],
    outreachActivities: [
      {
        activity: "Temporary Accommodation Visits",
        description:
          "Weekly visits to hotels and shelters housing asylum seekers and homeless families",
        delivery: "Mobile support team, multilingual staff",
        reach: "80+ families visited monthly",
      },
      {
        activity: "Community Hub Sessions",
        description:
          "Pop-up sessions at libraries, community centers, places of worship - bringing services to communities",
        delivery: "Weekly sessions across 5 locations",
        reach: "100+ community members monthly",
      },
      {
        activity: "Faith Community Partnership",
        description:
          "Working with mosques, churches, temples to reach faith communities with culturally appropriate support",
        delivery: "Info sessions, referral partnerships",
        reach: "50+ referrals monthly",
      },
      {
        activity: "School & Family Hub Outreach",
        description:
          "Presence at school events, family hubs to connect with parents facing barriers",
        delivery: "Termly events + ongoing partnerships",
        reach: "40+ families engaged",
      },
      {
        activity: "Digital Outreach",
        description:
          "WhatsApp groups in multiple languages, social media in community languages, text message updates",
        delivery: "Regular information sharing + signposting",
        reach: "200+ community members connected",
      },
    ],
    keyPrograms: {
      londonCommunityOutreachProject: {
        name: "Community Outreach Project",
        funding: "Greater London Authority (GLA)",
        description:
          "Empowering Londoners through Skills and Employment Support - targeted outreach to underserved communities",
        reach2024: "200+ active participants",
        outcomes: {
          education: "100+ enrolled in courses",
          employment: "45 secured employment or progression",
          advocacy: "120+ received support navigating systems",
        },
      },
    },
    impactMetrics: {
      totalReached: "750+ community members in 2024-25",
      activeEngagement: "200+ in ongoing support",
      newEnrollments: "150+ referred to APT services",
      partnershipSites: "12 community locations",
    },
    caseStudy: {
      scenario:
        "Albanian family in temporary hotel accommodation - isolated, unaware of services",
      outreach:
        "APT outreach team visiting hotel weekly, Albanian-speaking staff built trust over several visits",
      engagement:
        "Mother invited to women's group, then enrolled in ESOL. Father connected to job search support. Children linked to coding camp.",
      longTermImpact:
        "Family moved to permanent housing. Mother volunteering as peer supporter. Father secured employment. Family now refers other Albanian families to APT.",
      quote:
        "We didn't know help existed. APT came to us when we were alone and couldn't ask.",
    },
    whyOutreachMatters: [
      "Many facing greatest challenges don't know services exist",
      "Language barriers prevent accessing information",
      "Trust must be built - communities with trauma, negative experiences with authorities",
      "Transport, childcare, digital access create barriers to visiting offices",
      "Cultural norms may prevent asking for help - outreach normalizes support",
    ],
    partnerships: [
      "Local housing authorities",
      "Temporary accommodation providers",
      "Faith organizations (mosques, churches, temples)",
      "Community centers and libraries",
      "Schools and family hubs",
      "Integration Hubs network",
    ],
    accessInfo: {
      joinOutreachTeam: "Volunteer opportunities for community members",
      requestOutreach:
        "Organizations can request APT outreach at their site",
      languages: "Multilingual team and volunteer interpreters",
      cost: "All outreach services free",
    },
  },

  {
    id: "women-4-women",
    title: "Women 4 Women",
    shortDescription: "Empowering women entrepreneurs through community",
    description:
      "An inclusive, peer-led platform for women at every stage of enterprise — from a seed of an idea to a business you're proud of. Network, share, connect and grow together.",
    projectType: "enterprise-support",
    whoThisHelps: [
      "Women with business ideas seeking support",
      "Mums looking to build income and become role models",
      "Women testing new business concepts",
      "Established entrepreneurs ready to scale",
      "Women who don't want to build alone",
    ],
    servicesProvided: [
      {
        service: "Peer Learning & Networking",
        description:
          "Build real relationships in a warm, purposeful space. Meet women who understand your challenges and want you to succeed.",
        casesSupported2024: "Ongoing",
        outcomes: "Collaborations formed, friendships made, businesses supported",
      },
      {
        service: "Action Learning Sets",
        description:
          "Small, confidential groups where you bring real challenges. The group listens deeply and helps you find your own answers through powerful questions.",
        casesSupported2024: "Ongoing",
        outcomes: "Problems solved, confidence built, progress tracked",
      },
      {
        service: "Expert Speakers & Mentors",
        description:
          "Hear from professionals, established founders and guest experts. Sessions shaped collectively by the women in the room.",
        casesSupported2024: "Ongoing",
        outcomes: "Knowledge shared, doors opened, inspiration sparked",
      },
      {
        service: "SMART Goal Setting",
        description:
          "Every session closes with one concrete action — specific, achievable, yours. Gentle accountability from women who champion your progress.",
        casesSupported2024: "Ongoing",
        outcomes: "Goals achieved, businesses launched, income generated",
      },
    ],
    totalImpact2024: {
      casesSupported: "Growing community",
      financialBenefitsSecured: "Volunteer-run project",
      housingOutcomes: "Women building sustainable income",
      systemsNavigated: "Enterprise support, mentorship, peer networks",
    },
    caseStudy: {
      situation:
        "Single mum with a business idea but no support network and limited confidence",
      barriers:
        "Isolation, lack of business knowledge, childcare constraints, fear of failure",
      intervention:
        "Joined W4W community, attended peer learning sessions, received mentorship from established business owner",
      outcome:
        "Launched small business, generating income, now mentors other women in the community",
      quote:
        "I thought I had to do this alone. W4W showed me I don't — and now I'm helping other women see that too.",
    },
    partnerOrganizations: [
      "Local business mentors",
      "Enterprise support organisations",
      "Venue partners",
      "Pro-bono experts",
      "Funders and sponsors",
    ],
    accessInfo: {
      cost: "Free for all participants",
      languages: "English (multilingual support where possible)",
      contactMethods: "Email or contact form to register interest",
      urgentCases:
        "Open to women at any stage, from any background",
      referrals: "Self-referral welcome",
    },
  },
];

// Quick reference for navigation
export const CommunityProjectsCategories = [
  {
    id: "advocacy-services",
    title: "Advocacy Services",
    url: "/community-projects/advocacy-services",
  },
  {
    id: "community-outreach",
    title: "Community Outreach",
    url: "/community-projects/community-outreach",
  },
  {
    id: "women-4-women",
    title: "Women 4 Women",
    url: "/community-projects/women-4-women",
  },
];
